import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import Loader from "../../Global/Loader";

function EducationDetails({educationData, setActiveEducationPage, getUserDetails, onViewOrEdit}) {

  // State to track the modal visibility
  const [activeModal, setActiveModal] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Ref for modal
  const modalRef = useRef();

  // Function to toggle modal
  const toggleModal = (id) => {
    setActiveModal(activeModal === id ? null : id);
  };

  // Close the modal if clicked outside
  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setActiveModal(null);
    }
  };

  useEffect(() => {
    // Add event listener when the modal is open
    if (activeModal !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    // Cleanup event listener on unmount
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeModal]);

  const handleDeleteEducation = async (id) => {
    try {
        setIsLoading(true);
        const response = await axios.delete(`${process.env.REACT_APP_BASE_URL}/student/education/${id}`, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
        });
        if(response.status === 200) {
            // Refresh the education data after deletion
            getUserDetails();
        }
        // console.log(response.data);
        // Refresh the education data after deletion
        getUserDetails();
    } catch (error) {
        setIsLoading(false);
        // console.error(error);
    } finally{
        setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col space-y-6 w-[72%]">
    {isLoading && <Loader />}
      <div className="px-10 py-6 bg-white rounded shadow-lg">
        {/* Education Details Heading */}
        <h2 className="text-gray-800 text-lg font-semibold mb-4">
          Education Details
        </h2>
        <hr className="border-t border-gray-300 mb-8" />

        {/* Add Education Button */}
        <button 
            onClick={() => setActiveEducationPage('add-education')}
            className="flex items-center px-4 py-2 mb-6 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 5a1 1 0 011 1v3h3a1 1 0
              110 2h-3v3a1 1 0
              11-2 0v-3H6a1 1 0
              110-2h3V6a1 1 0
              011-1z"
              clipRule="evenodd"
            />
          </svg>
          Add Education
        </button>

        {/* Map over education data */}
        {educationData.map((edu) => (
          <div key={edu._id} className=" ml-10">
            <div className="flex items-start">
              {/* Timeline Icon + Line */}
              <div className="flex flex-col items-center">
                <div className="text-blue-500">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="8"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    />
                    <circle cx="12" cy="12" r="3" fill="currentColor" />
                    <path
                      d="M12 7v10"
                      stroke="currentColor"
                      strokeWidth="0.5"
                      strokeDasharray="2 2"
                    />
                  </svg>
                </div>
                {/* Gradient Bar */}
                <div className="w-0.5 h-36 bg-blue-200 mt-2" />
              </div>

              {/* Education Details */}
              <div className="ml-4 pt-14">
                <p className="text-gray-600 text-sm">{new Date(edu.endDate).getFullYear()}</p>
                <h3 className="text-gray-800 text-md font-semibold">
                  {edu.qualificationLevel}
                </h3>
                <p className="text-gray-600 text-base">{edu.programTitle}</p>
                <p className="text-gray-600 text-sm">{edu.instituteName}</p>
                <p className="text-gray-600 text-sm">
                  Grade/GPA/Marks: {edu.obtained}{edu.gradingSystem !== 'Percentage' && '/'}{edu.gradingSystem !== 'Percentage' && edu.total}{edu.gradingSystem === 'Percentage' && '%'}
                </p>
              </div>

              {/* Three Dot Button */}
              <div className="ml-auto flex flex-col justify-center pt-16 relative">
                <button
                  onClick={() => toggleModal(edu._id)}
                  className="text-blue-500 hover:text-blue-600 focus:outline-none rounded-full"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <circle cx="12" cy="5" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="12" cy="19" r="2" />
                  </svg>
                </button>

                {/* Modal (Visible only for the active item) */}
                {activeModal === edu._id && (
                  <div
                    ref={modalRef}
                    className="absolute top-[90px] right-[8px] bg-white rounded-lg border border-gray-300 shadow-lg w-52 py-4 px-4 z-50"
                  >
                    <div className="flex flex-col">
                      <a
                        onClick={() => {
                            onViewOrEdit(edu, "view");        // pass entire edu record to parent
                            setActiveModal(null);     // close the modal
                        }}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left flex items-center cursor-pointer"
                      >
                        <i className="fas fa-eye mr-2"></i> View
                      </a>
                      <a
                        onClick={() => {
                            onViewOrEdit(edu, "edit");        // pass entire edu record to parent
                            setActiveModal(null);     // close the modal
                        }}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left flex items-center cursor-pointer"
                      >
                        <i className="fas fa-edit mr-2"></i> Edit
                      </a>
                      <a
                        onClick={() => handleDeleteEducation(edu._id)}
                        className="px-4 py-2 text-gray-700 hover:bg-gray-100 w-full text-left flex items-center cursor-pointer"
                      >
                        <i className="fas fa-trash mr-2"></i> Delete
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EducationDetails;
